import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plane, Send, Loader2, CheckCircle2, Compass, ArrowUpRight } from "lucide-react";
import apiClient, { TRAVEL } from "@/lib/api";
import { toast } from "sonner";

const TRAVEL_IMAGE =
  "https://static.prod-images.emergentagent.com/jobs/4f4bec2b-62d9-44af-adaf-417050b8d780/images/09c12e2b8d5e0fccd0387b2ebcebda7364a7496b8d07d0ad95da1df0c51fd30f.jpeg";

const DESTINATIONS = [
  "Santorini", "Dubai", "Maldives", "Paris", "Bali", "Tulum",
  "Jamaica", "Amalfi Coast", "Tokyo", "Punta Cana", "Aruba", "Bahamas",
];

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } },
};

export default function TravelSection() {
  const [formOpen, setFormOpen] = useState(false);

  return (
    <section id="travel" data-testid="travel-section" className="lk-section px-0 bg-[#070604] border-y border-[#D4AF37]/10 overflow-hidden">
      {/* Destination marquee */}
      <div className="border-b border-[#D4AF37]/10 py-5 overflow-hidden select-none" aria-hidden="true">
        <motion.div
          className="flex gap-14 whitespace-nowrap will-change-transform"
          animate={{ x: ["0%", "-50%"] }}
          transition={{ duration: 45, ease: "linear", repeat: Infinity }}
        >
          {[...DESTINATIONS, ...DESTINATIONS].map((d, i) => (
            <span key={`${d}-${i}`} className="flex items-center gap-14 font-serif text-2xl md:text-3xl font-light text-zinc-600">
              <span className="italic hover:text-[#D4AF37] transition-colors">{d}</span>
              <span className="text-[#D4AF37]/40 text-sm">✦</span>
            </span>
          ))}
        </motion.div>
      </div>

      <div className="px-6 md:px-12 lg:px-24 pt-20 pb-24">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          {/* Left — editorial copy + image */}
          <motion.div
            className="lg:col-span-5"
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-80px" }}
            variants={fadeUp}
          >
            <span className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] flex items-center gap-2">
              <Plane className="w-3.5 h-3.5" /> Luxury Kingz Travel
            </span>
            <h2 className="font-serif text-4xl md:text-5xl font-light mt-4 leading-tight">
              The celebration
              <br />
              <span className="italic gold-gradient-text">shouldn't end here.</span>
            </h2>
            <p className="text-zinc-400 mt-6 leading-relaxed max-w-md">
              We're also a full-service travel agency. Honeymoons, birthday getaways, group retreats — tell us where
              you're dreaming of and we'll craft a personalized quote, or book your own escape in minutes.
            </p>
            <div className="relative mt-10 group">
              <div className="absolute -inset-2 border border-[#D4AF37]/20 translate-x-3 translate-y-3 pointer-events-none" />
              <motion.img
                src={TRAVEL_IMAGE}
                alt="Luxury travel — first class window over tropical islands"
                className="relative w-full h-64 md:h-72 object-cover"
                initial={{ scale: 1.08 }}
                whileInView={{ scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
              />
            </div>
          </motion.div>

          {/* Right — CTAs + quote form */}
          <motion.div
            className="lg:col-span-7 space-y-6"
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-80px" }}
            variants={fadeUp}
          >
            <div className="grid md:grid-cols-2 gap-6">
              <button
                onClick={() => setFormOpen((v) => !v)}
                data-testid="travel-quote-open-btn"
                className={`lk-card p-8 text-left group transition-colors ${formOpen ? "border-[#D4AF37]/60" : "hover:border-[#D4AF37]/40"}`}
              >
                <Compass className="w-6 h-6 text-[#D4AF37] mb-5 group-hover:rotate-45 transition-transform duration-500" />
                <h3 className="font-serif text-2xl mb-2">Get a Travel Quote</h3>
                <p className="text-sm text-zinc-400 leading-relaxed">
                  Tell us your destination & budget — we'll personally craft your perfect trip.
                </p>
                <span className="inline-block mt-5 text-xs uppercase tracking-[0.25em] text-[#D4AF37]">
                  {formOpen ? "Close Form ↑" : "Request Quote ↓"}
                </span>
              </button>

              <a
                href={TRAVEL.selfServiceHref}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="travel-self-service-link"
                className="lk-card p-8 text-left group hover:border-[#D4AF37]/40 transition-colors block"
              >
                <ArrowUpRight className="w-6 h-6 text-[#D4AF37] mb-5 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform duration-300" />
                <h3 className="font-serif text-2xl mb-2">Book Your Own Travel</h3>
                <p className="text-sm text-zinc-400 leading-relaxed">
                  Prefer self-service? Browse and book flights, cruises, resorts & more on our travel site.
                </p>
                <span className="inline-block mt-5 text-xs uppercase tracking-[0.25em] text-[#D4AF37]">
                  Self-Service →
                </span>
              </a>
            </div>

            <AnimatePresence initial={false}>
              {formOpen && (
                <motion.div
                  key="quote-form"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                  className="overflow-hidden"
                >
                  <QuoteForm onDone={() => setFormOpen(false)} />
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function QuoteForm({ onDone }) {
  const [form, setForm] = useState({ name: "", destination: "", budget: "", phone: "", email: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await apiClient.post("/travel-quotes", form);
      setSent(true);
      toast.success("Quote request received — we'll be in touch soon!");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not send your request. Please try again.");
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <div className="lk-card p-10 text-center" data-testid="travel-quote-success">
        <CheckCircle2 className="w-10 h-10 text-[#D4AF37] mx-auto mb-4" />
        <h3 className="font-serif text-2xl mb-2">Request received.</h3>
        <p className="text-sm text-zinc-400">
          We'll reach out with a personalized quote for {form.destination || "your trip"}. Safe travels ahead!
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="lk-card p-8 grid md:grid-cols-2 gap-x-8 gap-y-6" data-testid="travel-quote-form">
      <Field label="Where do you want to travel?" span>
        <input type="text" required value={form.destination} onChange={set("destination")} className="lk-input" placeholder="e.g. Maldives, Dubai, a Caribbean cruise…" data-testid="travel-destination-input" />
      </Field>
      <Field label="What's your ideal budget?" span>
        <input type="text" required value={form.budget} onChange={set("budget")} className="lk-input" placeholder="e.g. $2,500 – $4,000" data-testid="travel-budget-input" />
      </Field>
      <Field label="Your name">
        <input type="text" required minLength={2} value={form.name} onChange={set("name")} className="lk-input" data-testid="travel-name-input" />
      </Field>
      <Field label="Phone number">
        <input type="tel" required minLength={7} value={form.phone} onChange={set("phone")} className="lk-input" placeholder="+1 (___) ___-____" data-testid="travel-phone-input" />
      </Field>
      <Field label="Email" span>
        <input type="email" required value={form.email} onChange={set("email")} className="lk-input" data-testid="travel-email-input" />
      </Field>
      <div className="md:col-span-2 flex items-center justify-between flex-wrap gap-4 mt-2">
        <p className="text-xs text-zinc-500 max-w-sm">How would you like to be contacted? Leave both your number and email — we'll use whichever you prefer.</p>
        <button type="submit" disabled={sending} className="lk-btn-primary text-xs inline-flex items-center gap-2 disabled:opacity-50" data-testid="travel-quote-submit">
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          Request My Quote
        </button>
      </div>
    </form>
  );
}

function Field({ label, children, span }) {
  return (
    <div className={span ? "md:col-span-2" : ""}>
      <label className="text-xs uppercase tracking-widest text-[#D4AF37] mb-2 block">{label}</label>
      {children}
    </div>
  );
}
